import { computed, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { IAdditionGroup } from '@core/models/shop.model';
import { DoodProductModel } from '@core/models/product.model';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map } from 'rxjs';
import { ValidityWithAdditionsByGroupsState } from '../cart/util/cart-simple-product-util';
import { SimpleProductCartUtil } from './util/simple-product-stepper-util';
import { AutoFillCartItemRulesAdditionsGroup } from '../cart/auto-fill-cart-item-rules/auto-fill-cart-item-rules-additions-group';
import { CartDraftSimpleProductStore } from '../cart/cart-draft-simple-product.store';
import { SimpleProductWithAdditionsGroupStep, Stepper } from './stepper.type';
import { RootStepperStore } from './root-stepper.store';
import { StepInfo } from '@core/models/stepper.model';
import { ProductsService } from '@core/services/products/products.service';
import { AnalyticsService } from '@core/services/app/analytics.service';

export type SimpleProductConfigStepperData = {
  additionsByGroups: IAdditionGroup[];
  product: DoodProductModel;
  selectedAdditionIds: string[];
  cartValidity: ValidityWithAdditionsByGroupsState;
};

export type SimpleProductConfigStepper = Signal<SimpleProductConfigStepperData | undefined>;
export interface SimpleProductStepper {
  simpleProductStepper: Signal<SimpleProductWithAdditionsGroupStep | undefined>;
  setSelectedAdditionsGroupId: (id: string) => void;
}

@Injectable()
export class SimpleProductStepperStore implements SimpleProductStepper {
  private readonly cartDraftSimpleProductStore = inject(CartDraftSimpleProductStore);
  private readonly rootStepperStore = inject(RootStepperStore);
  private readonly productsService = inject(ProductsService);
  private readonly analyticsService = inject(AnalyticsService);

  constructor() {
    effect(
      () => {
        const stepper = this.stepper();

        this.rootStepperStore.setChildStepper(stepper);
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  private readonly productId = toSignal(
    toObservable(this.cartDraftSimpleProductStore.selectors).pipe(
      map(selectors => selectors?.draftSimpleProduct?.id),
      distinctUntilChanged(),
    ),
  );

  private allProducts = toSignal(this.productsService.selectAll$(), {
    initialValue: [],
  });

  private readonly selectedAdditionsGroupId = computed(() => {
    const productId = this.productId();
    // reset to undefined each time the product change
    if (!productId) {
      return undefined;
    }
    // ! go automatically to the first step
    return signal<string | undefined>(undefined);
  });

  public setSelectedAdditionsGroupId(id: string) {
    const groups = this.cartDraftSimpleProductStore.selectors()?.draftAdditionGroups;
    const navigableId = AutoFillCartItemRulesAdditionsGroup.resolveNavigableAdditionGroupId(
      groups,
      id,
    );
    if (!navigableId) {
      return;
    }
    this.selectedAdditionsGroupId()?.set(navigableId);
  }

  public readonly simpleProductStepper = computed<SimpleProductWithAdditionsGroupStep | undefined>(
    () => {
      const productId = this.productId();
      // reset to undefined each time the product change
      if (!productId) {
        return undefined;
      }

      const selectors = this.cartDraftSimpleProductStore.selectors();
      if (!selectors) {
        return undefined;
      }
      const selectedStepId = AutoFillCartItemRulesAdditionsGroup.resolveNavigableAdditionGroupId(
        selectors.draftAdditionGroups,
        this.selectedAdditionsGroupId()?.(),
      );
      if (!selectedStepId) {
        if (selectors.draftAdditionGroups.length > 0 && selectors.validity.isValid) {
          return this.readyToAddWithoutChoice(selectors);
        }
        return undefined;
      }

      return SimpleProductCartUtil.calculateSimpleProductStates({
        product: selectors.draftSimpleProduct,
        simpleProductAdditionsByGroups: selectors.draftAdditionGroups,
        selectedAdditionsGroupId: selectedStepId,
        isAdditionGroupValid: SimpleProductCartUtil.isSelectedGroupValid({
          selectedAdditionsGroupId: selectedStepId,
          cartValidity: selectors.validity,
        }),
        totalGroupAdditionsQuantity: SimpleProductCartUtil.getAdditionGroupTotalSelected({
          selectedAdditionIds: selectors.cartDraft.additions,
          simpleProductAdditionsByGroups: selectors.draftAdditionGroups,
          selectedAdditionsGroupId: selectedStepId,
        }),
        setSelectedStep: (stepsId: StepInfo) => {
          const additionsGroupId = stepsId.additionsGroupId;
          if (!additionsGroupId) {
            return;
          }
          this.setSelectedAdditionsGroupId(additionsGroupId);
        },
        currentStep: {
          additionsGroupId: selectedStepId,
        },
        allProducts: this.allProducts(),
        additionGroupsValidity: selectors.validity,
        _debugMissingStepDetected: message => {
          // should not append, but if it does, we have to investigate
          this.analyticsService.log({
            level: 'debug',
            label: '[DOOD-1026] Simple Product With addition groups - Missing Step',
            message,
          });
        },
      });
    },
  );

  public readonly stepper = computed<Stepper | undefined>(() => {
    const stepper = this.simpleProductStepper();

    if (!stepper) {
      return undefined;
    }

    return {
      step: stepper,
      childStepper: undefined,
    } satisfies Stepper;
  });

  // only used to remember to inject the store in the component
  public init() {
    return;
  }

  /**
   * Every addition group is already imposed. Keep a valid terminal step so the customer
   * can add the product, without rendering a choice screen.
   */
  private readyToAddWithoutChoice(selectors: {
    draftSimpleProduct: DoodProductModel;
    draftAdditionGroups: IAdditionGroup[];
  }): SimpleProductWithAdditionsGroupStep {
    return {
      type: 'simpleProductWithAdditionsGroup',
      additionGroup: selectors.draftAdditionGroups[0],
      product: selectors.draftSimpleProduct,
      selectedAdditionsGroupId: undefined,
      selectedStepId: undefined,
      isValid: true,
      canMakeMoreChoices: false,
      minStepCount: 0,
      maxStepCount: Infinity,
      count: 0,
      hasNextStep: false,
      hasPreviousStep: false,
      previousStepId: undefined,
      nextStepId: undefined,
      skippable: true,
      additionsGroups: selectors.draftAdditionGroups,
      additionGroupPosition: 0,
      nextStep: undefined,
      previousStep: undefined,
      setSelectedStep: () => undefined,
    };
  }
}
